import React from 'react';
import { Container, Flex, Grid, Text } from 'theme-ui';
import ArrowRight from '~/assets/images/icons/thick-arrow-right.svg';
import CollectionLink from '~/components/Collection/Link';

const getHeaderLayoutConfiguration = (title, subtitle, body) => {
  if ((!title && !subtitle && !body) || (!title && !subtitle) || (!title && !body) || (!subtitle && !body)) return {};

  if (title && subtitle && body)
    return {
      gridTemplateColumns: ['1fr'],
      gridTemplateAreas: [
        `
      'title'
      'subtitle'
      'body'`,
      ],
      gap: ['4.1rem 0', null, '2.5rem'],
    };
  if (title && subtitle) {
    return {
      gridTemplateColumns: ['1fr', 'minmax(auto, 60rem)', '32rem 60rem'],
      gridTemplateAreas: [`'subtitle' 'title'`, null, `'subtitle title'`],
      gap: ['4.1rem 0', null, '9.3rem 2rem'],
    };
  }

  if (title && body) {
    return {
      gridTemplateAreas: `'title' 'body'`,
      gap: ['1.6rem 0', null, '3rem 0'],
      textAlign: 'center',
      maxWidth: ['32rem', '54rem', '74.3rem'],
      mx: 'auto',
      h2: { fontSize: ['3rem', null, '4rem'] },
      h3: { fontSize: ['3rem', null, '4rem'] },
    };
  }

  if (subtitle && body)
    return {
      gridTemplateColumns: ['1fr', 'minmax(auto, 60rem)', '32rem 60rem'],
      gridTemplateAreas: [`'subtitle' 'body'`, null, `'subtitle body'`],
      gap: ['4.1rem 0', null, '9.3rem 2rem'],
    };

  return {
    gridTemplateColumns: ['1fr', 'minmax(auto, 60rem)', '32rem 60rem'],
    gridTemplateAreas: [`'subtitle' 'title' 'body'`, null, `'title title' 'subtitle body'`],
    gap: ['4.1rem 0', null, '9.3rem 2rem'],
  };
};

const SectionHeader = React.memo(function SectionHeader({
  title,
  mobileTitle,
  subtitle,
  body,
  linkSlug,
  collectionSection = false,
  productSection = false,
  forwardSx = {},
  textDirectionMobile,
  textDirection,
}) {
  const textDirectionMobileOrDefault = textDirectionMobile ?? textDirection;
  if (productSection) {
    return (
      <Container
        variant={collectionSection ? 'containerMargin' : 'customNavContainer'}
        sx={{
          pb: collectionSection ? '16px' : 0,
          pt: collectionSection ? [0, '6px', 0] : 0,
          marginBottom: !collectionSection && '2.4rem',
          width: !collectionSection && '100%',
          px: '2.4rem',
          ...forwardSx,
        }}
      >
        {collectionSection ? (
          <Text
            as="h2"
            variant="h3"
            sx={{
              fontSize: collectionSection ? '2rem' : ['clamp(2rem, 6vw, 3.2rem)', '4.4rem'],
              pb: collectionSection ? 'none' : 'var(--horizontal-inset)',
              borderBottom: collectionSection ? 0 : '1px solid',
              borderColor: 'cloud',
            }}
          >
            {collectionSection ? (
              <CollectionLink slug={linkSlug}>
                {title} <ArrowRight />
              </CollectionLink>
            ) : (
              `${title}`
            )}
          </Text>
        ) : (
          <Text
            as="h2"
            variant="h3"
            sx={{
              fontSize: collectionSection ? '2rem' : ['2.4rem', '3.2rem'],
              pb: collectionSection ? 'none' : '2.4rem',
              borderBottom: collectionSection ? 0 : '1px solid',
              borderColor: 'cloud',
            }}
          >
            {collectionSection ? (
              <CollectionLink slug={linkSlug}>
                {title} <ArrowRight />
              </CollectionLink>
            ) : (
              `${title}`
            )}
          </Text>
        )}
      </Container>
    );
  }
  if (!body) {
    return (
      <Container
        variant={collectionSection ? 'containerMargin' : 'customNavContainer'}
        sx={{
          ...forwardSx,
          px: ['2.4rem', null, '4rem'],
          m: [
            textDirectionMobileOrDefault?.toLowerCase() !== 'center' ? 0 : 'auto',
            textDirection?.toLowerCase() !== 'center' ? 0 : 'auto',
          ],

          pb: collectionSection ? '1.6rem' : 0,
          pt: collectionSection ? [0, '.6rem', 0] : 0,
          marginBottom: !collectionSection && ['2.4rem', '4.8rem'],
          width: '100%',
        }}
      >
        {collectionSection ? (
          <Text
            as="h2"
            variant="h3"
            sx={{
              fontSize: collectionSection ? '2rem' : ['clamp(2rem, 6vw, 3.2rem)', '4.4rem'],
              pb: collectionSection ? 'none' : 'var(--horizontal-inset)',
              borderBottom: collectionSection ? 0 : '1px solid grey',
            }}
          >
            <CollectionLink slug={linkSlug}>
              {title} <ArrowRight />
            </CollectionLink>
          </Text>
        ) : (
          <Flex
            sx={{
              textAlign: [textDirectionMobileOrDefault?.toLowerCase(), textDirection?.toLowerCase()],

              alignItems: [
                textDirectionMobileOrDefault?.toLowerCase() === 'center'
                  ? 'center'
                  : textDirectionMobileOrDefault?.toLowerCase() === 'left'
                  ? 'start'
                  : 'end',
                textDirection?.toLowerCase() === 'center'
                  ? 'center'
                  : textDirection?.toLowerCase() === 'left'
                  ? 'start'
                  : 'end',
              ],
              flexDirection: 'column',
            }}
          >
            {/* mobile title defaults to title */}
            {title && (
              <>
                <Text
                  as="h2"
                  sx={{
                    display: ['none', 'block'],
                    width: ['36rem', '68rem'],
                    p: 0,
                    variant: 'text.h3',
                    m: 0,
                    height: 'fit-content',
                    fontSize: ['3.6rem', '5.6rem'],
                    letterSpacing: ['-0.03em', '-0.04em'],
                    lineHeight: 0.9,
                    '& > p': {
                      my: 0,
                    },
                  }}
                  dangerouslySetInnerHTML={{ __html: title }}
                />
                <Text
                  as="h2"
                  sx={{
                    display: ['block', 'none'],
                    width: ['36rem', '68rem'],
                    p: 0,
                    variant: 'text.h3',
                    m: 0,
                    height: 'fit-content',
                    fontSize: ['3.6rem', '5.6rem'],
                    letterSpacing: ['-0.03em', '-0.04em'],
                    lineHeight: 0.9,
                    '& > p': {
                      my: 0,
                    },
                  }}
                  dangerouslySetInnerHTML={{ __html: mobileTitle }}
                />
              </>
            )}
            {subtitle && (
              <Text
                sx={{
                  mt: '1.6rem',
                  fontSize: '1.6rem',
                  lineHeight: '115%',
                  maxWidth: ['36.5rem', '55.22rem', '66.8rem'],
                  letterSpacing: '-.04rem',
                  '& > p': {
                    my: 0,
                  },
                }}
                dangerouslySetInnerHTML={{ __html: subtitle }}
              />
            )}
          </Flex>
        )}
      </Container>
    );
  }
  return (
    <Container as="header" sx={{ ...forwardSx }}>
      <Grid
        sx={{
          ...getHeaderLayoutConfiguration(title, subtitle, body),
          textAlign: 'center',
        }}
      >
        <Text
          as="h1"
          variant="h3"
          sx={{
            gridArea: 'title',
            fontSize: ['3rem', null, '4rem'],
            m: 0,
            '& > p': {
              my: 0,
            },
          }}
          dangerouslySetInnerHTML={{ __html: title }}
        />

        <Text
          as="h2"
          variant="h4"
          sx={{
            gridArea: 'subtitle',
            fontSize: 'clamp(1.8rem, 10.5vw, 2.2rem) !important',
            m: 0,
            '& > p': {
              my: 0,
            },
          }}
          dangerouslySetInnerHTML={{ __html: subtitle }}
        />

        {body && body.childMarkdownRemark ? (
          <div
            dangerouslySetInnerHTML={{ __html: body.childMarkdownRemark.html }}
            sx={{
              gridArea: 'body',
              textAlign: [textDirectionMobile?.toLowerCase() ?? 'left', textDirection?.toLowerCase() ?? 'left'],
              ol: { paddingInlineStart: ['1rem', 0] },
              fontSize: '1.6rem',
              lineHeight: '140%',
              letterSpacing: '0.02em',
              p: { mb: '1rem' },
              li: { mb: 1 },
              a: { textDecoration: 'underline' },
            }}
          />
        ) : null}

        {body && typeof body === 'string' ? (
          <Text
            as="p"
            sx={{
              gridArea: 'body',
              display: body ? 'block' : 'none',
              p: { m: 0 },
            }}
          >
            {body}
          </Text>
        ) : null}
      </Grid>
    </Container>
  );
});

SectionHeader.defaultProps = {
  title: '',
  subtitle: '',
  body: '',
};

export default SectionHeader;
